// frontend/src/components/PageHeader.js
// The page's title block. The heading and default subtitle come from
// content/modules.js, so a rename lands in the sidebar and on the page at once.
// Pages must NOT hardcode their own <h1> — every page used to, which is how
// "Evaluations" ended up heading a page titled "Performance evaluation".
//
// This renders exactly the <div><h1/><p/></div> it replaces, so a page's
// surrounding flex wrapper and action buttons stay as they are.
import React from 'react';
import { Link } from 'react-router-dom';
import { moduleByKey } from '../content/modules';

/**
 * @param moduleKey  registry key — supplies the heading and the default subtitle
 * @param subtitle   override when the subtitle is dynamic (cycle name, counts,
 *                   mode). Pass null for no subtitle; omit for the registry's
 *                   `purpose`.
 * @param helpTask   guide task id — renders a ? linking to /guide#task-<id>.
 *                   The app's only help affordance, so it's worth adding
 *                   wherever a page has a task that explains it.
 */
export default function PageHeader({ moduleKey, subtitle, helpTask }) {
  const mod = moduleByKey(moduleKey);
  const sub = subtitle === undefined ? mod.purpose : subtitle;
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <h1 style={{ fontSize: 22 }}>{mod.label}</h1>
        {helpTask && (
          <Link to={`/guide#task-${helpTask}`} title={`How to use ${mod.label}`}
            style={{
              textDecoration: 'none', width: 18, height: 18, borderRadius: '50%',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              background: 'var(--gray-100)', color: 'var(--text-3)',
              fontSize: 11, fontWeight: 600, flexShrink: 0,
            }}>?</Link>
        )}
      </div>
      {sub && <p style={{ fontSize: 13, color: 'var(--text-3)', marginTop: 3 }}>{sub}</p>}
    </div>
  );
}

/**
 * The view-only notice for a manager on a page whose writes belong to admins and
 * department heads. The sidebar now advertises these pages to managers — they can
 * read them — so the absence of buttons needs explaining.
 */
export function ViewOnlyNotice({ children }) {
  return (
    <div style={{
      background: 'var(--navy-50)', borderRadius: 8, padding: '10px 14px',
      marginBottom: 14, fontSize: 12, color: 'var(--navy-600)',
    }}>
      {children || 'You have view-only access here. Ask an admin to make changes.'}
    </div>
  );
}
